// roll-insert.ts — handleRollInsert is the primary WS push receiver for new rolls.
// Defers to log-row-helpers for DOM ops + roll-group for the group-bump path.

import type { WSMessage } from './types';
import {
    FT_LOG_VISIBLE_ROW_CAP,
    _ftRecentRollIsForDedupe,
    _ftRecentRollIsOrder,
    _ftFindLogRowByRollId,
    _ftStripPrevRowTimestampIfSameRollId,
    prependLogRow,
    trimLogRows,
} from './log-row-helpers';
import { reconcileHeldRollGroup, replaceRollGroup } from './roll-group';
import type { Roll } from '../log/types';
import { handleStartTurnNotification } from './start-turn-gm';
import { handleCombatStartPrompt } from './combat-start-prompt';
import { playRollSound } from './roll-audio-mixer';
import { invalidatePaoeEntryRecords, rememberPaoeEntryLogRows } from './paoe-entry-records.js';
import { PAOE_ENTRY_RECORD } from '../janus-engine/ability/paoe-entry-once.js';
import { normalizeRollVfx, type RollVfxTypes } from '../janus-engine/presentation/roll-vfx-cue';

type WSRollInsertMessage = Extract<WSMessage, { type: 'roll_insert' }>;

// Presentation fan-out for surfaces that want to show an event without owning the log DOM (the FT3D
// campaign event feed is the first). This fires only on paths that already cleared the stale-encounter
// drop, the roll_i dedupe and the grouping decision, so a listener inherits every authorization gate
// instead of re-deriving them. It is a sibling of playRollSound, not a replacement for the log: the
// canonical Roll payload rides along, and `html` is the already-redacted row markup when the JS
// renderer produced one, so a consumer can take display text without re-implementing private-roll
// redaction. Listeners must treat this as read-only presentation.
function publishAuthorizedRoll(
    roll: unknown,
    html: string,
    grouped: boolean,
): void {
    try {
        document.dispatchEvent(new CustomEvent('ft-roll-authorized', {
            detail: { roll, html, grouped },
        }));
    } catch { /* presentation fan-out must never break log insertion */ }
}

function authorizedPresentationHtml(roll: WSRollInsertMessage['roll']): string {
    if (typeof window.renderSimpleLogRow !== 'function') return '';
    try {
        // Grouped rows are intentionally not inserted as another legacy-log card, but presentation
        // siblings still need the renderer's authorized/redacted markup. In particular, a damage
        // row normally shares the cast's roll_id; publishing an empty string made it disappear from
        // the FT3D event feed even though the canonical row existed.
        return window.renderSimpleLogRow(roll, { inline: true }) || '';
    } catch {
        return '';
    }
}

// Every row of a roll_id group this page first showed LIVE, newest first (fetch_rolls' roll_i DESC). A later member re-renders
// only that group through buildLog's own window renderer: a grouped cast/damage burst never re-reads and repaints the log.
const liveGroups = new Map<string, WSRollInsertMessage['roll'][]>();
// Groups that have drawn rows. One of these with no tagged rows left was replaced or trimmed; a silent group never drew any.
const shownGroups = new Set<string>();
const LIVE_GROUP_CAP = 60;
const liveNodes = (rid: string): HTMLElement[] => {
    const ul = document.querySelector('#div_sidelog_results > ul.list-group');
    if (!ul) return [];
    const safe = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(rid) : rid.replace(/"/g, '\\"');
    return [...ul.querySelectorAll<HTMLElement>(`:scope > li[data-ft-live-group="${safe}"]`)];
};
function rememberLiveGroup(rid: string, rolls: WSRollInsertMessage['roll'][]): void {
    liveGroups.delete(rid);
    liveGroups.set(rid, rolls);
    while (liveGroups.size > LIVE_GROUP_CAP) { const oldest = liveGroups.keys().next().value!; liveGroups.delete(oldest); shownGroups.delete(oldest); }
}
/** Render the live group with `roll` added and put it where the group's rows are (on top for a group nothing rendered yet).
 *  False leaves the caller's ordinary path in charge: no renderer, or the group's rows were trimmed out of the window. */
function renderLiveGroup(rid: string, roll: WSRollInsertMessage['roll']): boolean {
    const rolls = liveGroups.get(rid), render = window.renderLogWindowFromJson;
    if (!rolls || typeof render !== 'function') return false;
    const old = liveNodes(rid);
    if (!old.length && shownGroups.has(rid)) { liveGroups.delete(rid); shownGroups.delete(rid); return false; }
    if (!replaceRollGroup(rid, [roll, ...rolls] as unknown as Roll[], old)) return false;
    rolls.unshift(roll);
    if (liveNodes(rid).length) shownGroups.add(rid);
    return true;
}

async function handleRollInsert(msg: WSRollInsertMessage): Promise<void> {
    ftDebugLog('roll', { phase: 'insert' });

    // Per-frame audio trace (silent unless the 'audio' debug channel is on).
    if (msg?.roll) {
        const _r = msg.roll as Record<string, unknown>;
        ftDebugLog('audio', {
            phase: 'frame',
            roll_i: _r.roll_i,
            roll_type_1: _r.roll_type_1,
            roll_sound: _r.roll_sound ?? null,
        });
    }

    // Defense-in-depth encounter gate for players: drops messages from a stale-encounter
    // subscription that arrives during the race window between unsubscribe-old and
    // subscribe-new on encounter rotation. The publisher tags every roll_insert with
    // `scope: 'campaign' | 'encounter'` (see insert_rolls_inc.php) — we just trust that
    // tag instead of maintaining a parallel JS whitelist of roll_type_1 values.
    //
    // Bypasses:
    //   - GM viewer: gets everything via :gm by design, encounter doesn't gate.
    //   - msg.scope === 'campaign': server already decided this should reach every
    //     player regardless of their current encounter (push_campaign_time etc.).
    //   - msg has no encounter_id: nothing to compare against.
    const _ftIsGmViewer = !!(window.FateTide?.user?.isGM);
    const _ftMsgEncounterId = msg?.roll?.encounter_id || '';
    const _ftMsgIsCampaignScope = (msg?.scope === 'campaign');
    if (!_ftIsGmViewer && !_ftMsgIsCampaignScope && _ftMsgEncounterId) {
        const _ftCurEncounterId = String(window.ftGetActiveEncounterId?.() || '').trim();
        if (_ftCurEncounterId && _ftMsgEncounterId !== _ftCurEncounterId) {
            ftDebugLog('roll', { phase: 'insert-stale-encounter-drop', msgEnc: _ftMsgEncounterId, curEnc: _ftCurEncounterId });
            return;
        }
    }

    // Every saved roll can spend the entry/end-turn budget. Markers carry no log presentation.
    rememberPaoeEntryLogRows([msg.roll]);
    if (_ftMsgEncounterId) invalidatePaoeEntryRecords(_ftMsgEncounterId);
    if (msg.roll?.roll_type_1 === PAOE_ENTRY_RECORD) return;

    // Dedupe on roll_i (auto-increment PK), NOT roll_id. Grouped sessions
    // (mass-contest, death-session, trade-session) intentionally share one roll_id
    // across N rows; dedupe-by-roll_id would swallow every row past the first.
    // Defensive against e.g. a brief :gm + :player dual-subscribe during reconnect.
    const rollI = msg?.roll?.roll_i;
    if (rollI != null) {
        const key = String(rollI);
        if (_ftRecentRollIsForDedupe.has(key)) {
            ftDebugLog('roll', { phase: 'insert-dedupe', rollI });
            return;
        }
        _ftRecentRollIsForDedupe.add(key);
        _ftRecentRollIsOrder.push(key);
        if (_ftRecentRollIsOrder.length > 200) {
            const oldest = _ftRecentRollIsOrder.shift();
            if (oldest !== undefined) _ftRecentRollIsForDedupe.delete(oldest);
        }
    }

    // One native cue/clock result reaches the mixer, gesture and HUD, including grouped rows.
    if (msg.roll) normalizeRollVfx(msg.roll, (window as unknown as { ROLLTYPE_CATALOG?: RollVfxTypes }).ROLLTYPE_CATALOG || {});

    // Start-of-turn chime is a per-viewer notification: it fires only when the started
    // creature is the viewer's active caster (the pulled-up sheet), or when the GM
    // auto-opens the up-next NPC's card. Returns true only when it played the chime, so
    // we skip this frame's own playRollSound below.
    const startTurnChimePlayed = handleStartTurnNotification(msg.roll as unknown as Record<string, unknown> | undefined);

    // A GM "roll initiative?" prompt (spotted / attacked / lost a contest) stops campaign time
    // while the GM decides — arrival-time only, never on a history re-render. See
    // combat-start-prompt.ts.
    handleCombatStartPrompt(msg.roll as unknown as Record<string, unknown> | undefined);

    // The frame carries its sound ROUTE (`roll_sound`, insert_roll.php) — never a chosen file. playRollSound
    // picks the take from the page's audio catalog, seeded by the row so every client hears the same one, and
    // plays it across the effect-player pool so concurrent combat sounds layer instead of overriding.
    // Started HERE, after the dedupe and before any render await: its arrival snapshot must see the encounter as
    // the event left it, and frames must reach the resolver queue in arrival order (a buildLog() await let a later
    // row claim the cast's incantation and a damage update rewrite the pre-hit vitality — PRES review 2026-09-27).
    if (!startTurnChimePlayed && msg.roll && msg.roll.roll_sound) {
        void playRollSound(msg.roll as unknown as Record<string, unknown>);
    }

    // Live groups keep their held members; cold history uses the rows received by
    // buildLog's history event. Successful specialized container appends stay in place.
    const rollId = msg?.roll?.roll_id;
    if (rollId && liveGroups.has(rollId) && renderLiveGroup(rollId, msg.roll)) {
        publishAuthorizedRoll(msg.roll, authorizedPresentationHtml(msg.roll), true);
        ftDebugLog('roll', { phase: 'live-group-rendered', rollI, rollId });
        return;
    }
    if (rollId) {
        const existing = _ftFindLogRowByRollId(rollId);
        if (existing) {
            // Phase 4 — client-side in-place regrouping. When the existing visible row
            // is a server-rendered grouped container (no data-ft-roll-group-state attr,
            // e.g. mass-contest-rollup), slot the new row INSIDE the existing collapse
            // instead of bumping the spinner / debouncing buildLog. Saves a full window
            // AJAX refresh for late-arriving group rows.
            if (existing.dataset.ftRollGroupState === undefined
                && typeof window._ftAppendToExistingGroup === 'function'
                && window._ftAppendToExistingGroup(existing, msg)) {
                publishAuthorizedRoll(msg.roll, authorizedPresentationHtml(msg.roll), true);
                ftDebugLog('roll', { phase: 'phase4-appended-in-place', rollI, rollId });
                return;
            }
            reconcileHeldRollGroup(rollId, msg.roll as unknown as Roll);
            // The absorbed roll's audio already started above — the user still gets the cue.
            publishAuthorizedRoll(msg.roll, authorizedPresentationHtml(msg.roll), true);
            ftDebugLog('roll', { phase: 'grouped-into-existing', rollI, rollId });
            return;
        }
    }

    if (typeof window.renderSimpleLogRow === 'function') {
        // Server publishes pure JSON ({ type:'roll_insert', roll }); the JS renderer
        // in includes/js-src/log/handlers.ts handles this type from the JSON payload.
        // renderSimpleLogRow returns '' if the type isn't in its dispatch table —
        // falls through to buildLog() as the safety net.
        const jsHtml = window.renderSimpleLogRow(msg.roll);
        if (jsHtml) {
            _ftStripPrevRowTimestampIfSameRollId(rollId);
            prependLogRow(jsHtml);
            trimLogRows(FT_LOG_VISIBLE_ROW_CAP);
            // Remember a fresh single row so later members reconcile only its group.
            if (rollId) {
                const ulNow = document.querySelector('#div_sidelog_results > ul.list-group');
                if (ulNow) {
                    const first = ulNow.firstElementChild as HTMLElement | null;
                    if (first && first.getAttribute('data-ft-roll-id') === rollId) {
                        first.dataset.ftRollGroupState = 'single';
                        first.dataset.ftLiveGroup = rollId;
                        rememberLiveGroup(rollId, [msg.roll]);
                        shownGroups.add(rollId);
                    }
                }
            }
            publishAuthorizedRoll(msg.roll, jsHtml, false);
        } else if (rollId && typeof window.renderLogWindowFromJson === 'function') {
            // No simple row: a container type (contest, death, trade, gate rollup) or a silent row. The window renderer draws exactly
            // what buildLog would for it, here and without a log read; a silent row draws nothing until a later member renders.
            rememberLiveGroup(rollId, []);
            if (!renderLiveGroup(rollId, msg.roll)) liveGroups.delete(rollId);
            publishAuthorizedRoll(msg.roll, '', false);
        } else if (typeof buildLog === 'function') {
            await buildLog();
            publishAuthorizedRoll(msg.roll, '', false);
        }
    } else if (typeof buildLog === 'function') {
        await buildLog();
        publishAuthorizedRoll(msg.roll, '', false);
    }

    ftDebugLog('roll', { phase: 'inserted', rollI });
}

export { handleRollInsert };

// The in-page truth sink publishes through the ordinary receiver, with no socket or log fetch.
declare global { interface Window { ftHandleRollInsert?: typeof handleRollInsert } }
if (typeof window !== 'undefined') window.ftHandleRollInsert = handleRollInsert;
